import { Component } from '@angular/core';
import { Queue } from 'api/types';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { Filter } from 'types/Filter';
import { formatQueueDisplay } from 'utils/format-queue';
import { PoolConsumptionFiltersComponent } from '../poolconsumption-filters/poolconsumption-filters.component';
import { GetPoolConsumptionParameters } from 'api/types/endpoints/getPoolConsumption';
import { PoolConsumptionPageDataService } from 'services/poolconsumption-page-data.service';
import { QueuesService } from 'services/api/queues.service';
import { TranslatePipe } from 'pipes/translate.pipe';
import { UnderutilizationPageDataService } from 'services/underutilization-page-data.service';

export interface PoolConsumptionFilters {
  queue: Filter<Queue>;
}
@Component({
  selector: 'app-underutilization-filters',
  standalone: false,
  templateUrl: './underutilization-filters.component.html',
  styleUrl: './underutilization-filters.component.scss'
})



export class UnderutilizationFiltersComponent {

/**
   * Used to signal the special "All Queues" option
   */
   private static readonly ALL_QUEUES_CODE = 'ALLQUEUES';

  /**
   * Initial search term
   */
  public initialSearchTerm = '';

  /**
   * How the queue should look as a dropdown option
   */
  public getQueueDisplay = formatQueueDisplay;

  /**
   * List of available queues
   */
  public queues: Queue[] = [];

  /**
   * Queue filter
   */
  public filters: PoolConsumptionFilters = {
    queue: {
      enabled: true,
    }
  };

  public constructor(
    private poolConsumptionPageDataService: UnderutilizationPageDataService,
    private queuesService: QueuesService,
    private translatePipe: TranslatePipe,
  ) {
    // Load queues & translation & previous filters
    combineLatest([
      this.queuesService.getQueues(),
      this.translatePipe.loadTranslations([ 'option.allQueues' ]),
      this.poolConsumptionPageDataService.params$
    ])
      .pipe(take(1))
      .subscribe(([ queueResponse, translations, params ]) => {
        // Create a filter option for all queues
        const allQueuesOption: Queue = {
          id: 'all',
          code: UnderutilizationFiltersComponent.ALL_QUEUES_CODE,
          name: translations[ 'option.allQueues' ]
        };
        this.initialSearchTerm = params?.searchTerm || '';
        this.queues = [ allQueuesOption, ...queueResponse ];

        // Set initial queue from service or default to all queues
        this.filters.queue.value = this.queues.find((q) => params?.queueIds?.includes(q.id)) ?? allQueuesOption;
        this.filters.queue.options = this.queues;
      });
  }

  /**
   * Update client search param
   */
  public updateSearchParams(inputValue: string): void {
    const newParams: Partial<GetPoolConsumptionParameters> = {
      searchTerm: inputValue,
    };
    this.poolConsumptionPageDataService.updateParams(newParams);
  }

  /**
   * Handle change in queue filter
   *
   * Note: there is only a single filter, so `filterItem` is ignored.
   */
  public filterChanged(filterItem: string, queueInfo?: Queue): void {
    // Pass empty array for all queues
    const ids = !queueInfo || queueInfo.code === UnderutilizationFiltersComponent.ALL_QUEUES_CODE ? [] : [ queueInfo.id ];
    const newParams: Partial<GetPoolConsumptionParameters> = {
      queueIds: ids,
    };
    this.poolConsumptionPageDataService.updateParams(newParams);
  }
}

